import React, { useEffect, useState } from 'react';
import { getServices, createService, updateService, deleteService } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../utils/apiError';
import { Plus, Scissors, Pencil, Trash2 } from 'lucide-react';

const DURATION_OPTIONS = [15, 30, 45, 60];

export default function Services() {
  const [services, setServices] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    duration_minutes: '30',
    price: '',
    sort_order: '0',
    is_active: true,
  });

  const fetchServices = async () => {
    try {
      const response = await getServices(true);
      const list = Array.isArray(response.data?.data) ? response.data.data : (Array.isArray(response.data) ? response.data : []);
      setServices(list);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al cargar servicios'));
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleOpenDrawer = (service = null) => {
    setSelectedService(service);
    if (service) {
      setFormData({
        name: service.name || '',
        duration_minutes: String(service.duration_minutes || 30),
        price: service.price != null ? String(service.price) : '',
        sort_order: String(service.sort_order || 0),
        is_active: Boolean(service.is_active),
      });
    } else {
      setFormData({
        name: '',
        duration_minutes: '30',
        price: '',
        sort_order: '0',
        is_active: true,
      });
    }
    setDrawerOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        duration_minutes: Number(formData.duration_minutes),
        price: formData.price === '' ? null : Number(formData.price),
        sort_order: Number(formData.sort_order || 0),
        is_active: Boolean(formData.is_active),
      };

      if (selectedService) {
        await updateService(selectedService.id, payload);
        toast.success('Servicio actualizado');
      } else {
        await createService(payload);
        toast.success('Servicio creado');
      }
      setDrawerOpen(false);
      fetchServices();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al guardar servicio'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeactivate = async (serviceId) => {
    if (!window.confirm('¿Desactivar este servicio?')) return;
    try {
      await deleteService(serviceId);
      toast.success('Servicio desactivado');
      fetchServices();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al desactivar servicio'));
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#2B2D42] font-['Manrope']">Servicios</h1>
          <p className="text-[#5D5C61] mt-1">Administra el catalogo de servicios del negocio</p>
        </div>
        <Button
          className="bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
          onClick={() => handleOpenDrawer()}
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo servicio
        </Button>
      </div>

      <div className="grid gap-4">
        {services.map((svc) => (
          <Card key={svc.id} className="bg-white border-slate-100">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#335C67] flex items-center justify-center text-white">
                    <Scissors className="w-4 h-4" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{svc.name}</CardTitle>
                    <CardDescription>
                      {svc.duration_minutes} min{svc.price != null ? ` · $${Number(svc.price).toFixed(2)}` : ''}
                    </CardDescription>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${svc.is_active ? 'bg-[#588157]/10 text-[#588157]' : 'bg-slate-200 text-slate-600'}`}>
                  {svc.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 pt-2">
                <Button variant="ghost" size="sm" onClick={() => handleOpenDrawer(svc)}>
                  <Pencil className="w-4 h-4 mr-1" />
                  Editar
                </Button>
                {svc.is_active && (
                  <Button variant="ghost" size="sm" className="text-[#9E2A2B] hover:bg-[#9E2A2B]/10" onClick={() => handleDeactivate(svc.id)}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Desactivar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="text-2xl font-['Manrope']">
              {selectedService ? 'Editar servicio' : 'Nuevo servicio'}
            </SheetTitle>
            <SheetDescription>
              {selectedService ? 'Actualiza la configuracion del servicio' : 'Crea un servicio para poder agendarlo'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <div className="space-y-2">
              <Label>Nombre del servicio</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Corte clasico"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Duracion (minutos)</Label>
              <Select
                value={String(formData.duration_minutes)}
                onValueChange={(value) => setFormData({ ...formData, duration_minutes: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map((minutes) => (
                    <SelectItem key={minutes} value={String(minutes)}>
                      {minutes} minutos
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Coste del servicio</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="Ej. 250.00"
              />
            </div>

            <div className="space-y-2">
              <Label>Orden</Label>
              <Input
                type="number"
                min="0"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
              />
            </div>

            <div className="flex items-center justify-between py-4 px-4 bg-[#F4F1EA] rounded-lg">
              <div>
                <Label className="font-medium">Servicio activo</Label>
                <p className="text-sm text-[#5D5C61]">Disponible para agendar</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <Button type="submit" className="w-full bg-[#335C67] hover:bg-[#244048] text-white rounded-full" disabled={isLoading}>
              {isLoading ? 'Guardando...' : 'Guardar'}
            </Button>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}

